<template>
  <main-layout>
    <template #header>
      <main-header :title="'드라이버 설치' | translate" />
    </template>
    <template #body>
      <div class="driver-page">
        <p class="hint">
          {{
            '프린터·스캐너 설치 파일 주소를 등록합니다. 에이전트는 제조사와 모델이 맞는 사용 중 항목의 URL로 패키지를 받아 설치합니다.'
              | translate
          }}
        </p>
        <section class="todo">
          <div class="todo-head">
            <h3>{{ '드라이버 신청 할 일' | translate }}</h3>
            <div class="tools">
              <button type="button" class="btn outline" :class="{ on: requestStatus === 'OPEN' }" @click="setRequestStatus('OPEN')">
                {{ '대기' | translate }}
              </button>
              <button type="button" class="btn outline" :class="{ on: requestStatus === 'DONE' }" @click="setRequestStatus('DONE')">
                {{ '완료' | translate }}
              </button>
              <button type="button" class="btn outline" :class="{ on: requestStatus === '' }" @click="setRequestStatus('')">
                {{ '전체' | translate }}
              </button>
            </div>
          </div>
          <div class="table-wrap">
            <table class="list-table">
              <thead>
                <tr>
                  <th>{{ '상태' | translate }}</th>
                  <th>{{ '제조사' | translate }}</th>
                  <th>{{ '모델' | translate }}</th>
                  <th>{{ '종류' | translate }}</th>
                  <th>{{ '시리얼' | translate }}</th>
                  <th>{{ '거래처' | translate }}</th>
                  <th>{{ '신청일시' | translate }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!requests.length">
                  <td colspan="8" class="empty">{{ '신청 이력이 없습니다.' | translate }}</td>
                </tr>
                <tr v-for="row in requests" :key="row.printerDriverRequestId">
                  <td>{{ row.status === 'DONE' ? t('완료') : t('대기') }}</td>
                  <td>{{ row.manufacturer }}</td>
                  <td>{{ row.model }}</td>
                  <td>{{ kindLabel(row.kind) }}</td>
                  <td>{{ row.printerSerial || '-' }}</td>
                  <td>{{ row.accountId || '-' }}</td>
                  <td class="nowrap">{{ row.registerDatetime || '-' }}</td>
                  <td class="actions">
                    <button
                      v-if="row.status !== 'DONE'"
                      type="button"
                      class="link-btn"
                      @click="completeRequest(row)"
                    >
                      {{ '완료' | translate }}
                    </button>
                    <button v-else type="button" class="link-btn" @click="reopenRequest(row)">
                      {{ '대기로' | translate }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        <div class="tools">
          <input
            v-model="keyword"
            type="text"
            class="search-input"
            :placeholder="'제조사 / 모델 / URL / 드라이버 이름' | translate"
            @keydown.enter.prevent="search"
          />
          <select v-model="kind" class="filter" @change="search">
            <option value="">{{ '종류 전체' | translate }}</option>
            <option value="printer">{{ '프린터' | translate }}</option>
            <option value="scanner">{{ '스캐너' | translate }}</option>
          </select>
          <select v-model="architecture" class="filter" @change="search">
            <option value="">{{ '아키텍처 전체' | translate }}</option>
            <option value="common">{{ '공통' | translate }}</option>
            <option value="x64">x64</option>
            <option value="x86">x86</option>
            <option value="arm64">arm64</option>
          </select>
          <select v-model="useYn" class="filter" @change="search">
            <option value="">{{ '사용 여부 전체' | translate }}</option>
            <option value="Y">{{ '사용' | translate }}</option>
            <option value="N">{{ '중지' | translate }}</option>
          </select>
          <button type="button" class="btn" @click="search">{{ '검색' | translate }}</button>
          <button type="button" class="btn" @click="openCreate">{{ '등록' | translate }}</button>
        </div>
        <div class="table-wrap">
          <table class="list-table">
            <thead>
              <tr>
                <th>{{ '제조사' | translate }}</th>
                <th>{{ '모델' | translate }}</th>
                <th>{{ '종류' | translate }}</th>
                <th>{{ '아키텍처' | translate }}</th>
                <th>{{ '다운로드 URL' | translate }}</th>
                <th>{{ '파일명' | translate }}</th>
                <th>{{ '드라이버 이름' | translate }}</th>
                <th>{{ 'INF' | translate }}</th>
                <th>{{ '사용' | translate }}</th>
                <th>{{ '수정일시' | translate }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!rows.length">
                <td colspan="11" class="empty">{{ '등록된 드라이버가 없습니다.' | translate }}</td>
              </tr>
              <tr v-for="row in rows" :key="row.printerDriverPackageId">
                <td>{{ row.manufacturer }}</td>
                <td>{{ row.model }}</td>
                <td>{{ kindLabel(row.kind) }}</td>
                <td>{{ architectureLabel(row.architecture) }}</td>
                <td class="url" :title="row.downloadUrl">{{ row.downloadUrl }}</td>
                <td>{{ row.fileName || '-' }}</td>
                <td>{{ row.driverName || '-' }}</td>
                <td>{{ row.infFileName || '-' }}</td>
                <td>{{ row.useYn === 'N' ? t('중지') : t('사용') }}</td>
                <td class="nowrap">{{ row.updateDatetime || row.registerDatetime || '-' }}</td>
                <td class="actions">
                  <button type="button" class="link-btn" @click="openEdit(row)">{{ '수정' | translate }}</button>
                  <button type="button" class="link-btn danger" @click="remove(row)">{{ '삭제' | translate }}</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pager">
          <button type="button" class="btn outline" :disabled="page <= 1" @click="changePage(page - 1)">
            {{ '이전' | translate }}
          </button>
          <span>{{ page }} / {{ totalPages }}</span>
          <button
            type="button"
            class="btn outline"
            :disabled="page >= totalPages"
            @click="changePage(page + 1)"
          >
            {{ '다음' | translate }}
          </button>
        </div>
      </div>

      <div v-if="formOpen" class="modal-backdrop" @click.self="closeForm">
        <form class="modal" @submit.prevent="save">
          <h3>{{ formTitle }}</h3>
          <label>
            <span>{{ '제조사' | translate }}</span>
            <input v-model.trim="form.manufacturer" type="text" maxlength="100" required />
          </label>
          <label>
            <span>{{ '모델' | translate }}</span>
            <input v-model.trim="form.model" type="text" maxlength="200" required />
          </label>
          <div class="split">
            <label>
              <span>{{ '종류' | translate }}</span>
              <select v-model="form.kind">
                <option value="printer">{{ '프린터' | translate }}</option>
                <option value="scanner">{{ '스캐너' | translate }}</option>
              </select>
            </label>
            <label>
              <span>{{ '아키텍처' | translate }}</span>
              <select v-model="form.architecture">
                <option value="">{{ '공통' | translate }}</option>
                <option value="x64">x64</option>
                <option value="x86">x86</option>
                <option value="arm64">arm64</option>
              </select>
            </label>
          </div>
          <label>
            <span>{{ '다운로드 URL' | translate }}</span>
            <input v-model.trim="form.downloadUrl" type="url" maxlength="1000" required placeholder="https://" />
          </label>
          <label>
            <span>{{ '파일명' | translate }}</span>
            <input v-model.trim="form.fileName" type="text" maxlength="255" />
          </label>
          <label>
            <span>SHA-256</span>
            <input v-model.trim="form.sha256" type="text" maxlength="64" spellcheck="false" />
          </label>
          <div class="split">
            <label>
              <span>{{ '드라이버 이름' | translate }}</span>
              <input v-model.trim="form.driverName" type="text" maxlength="255" />
            </label>
            <label>
              <span>{{ 'INF 파일명' | translate }}</span>
              <input v-model.trim="form.infFileName" type="text" maxlength="255" />
            </label>
          </div>
          <label class="check">
            <input v-model="form.useYn" type="checkbox" true-value="Y" false-value="N" />
            <span>{{ '사용' | translate }}</span>
          </label>
          <p class="form-hint">
            {{ '아키텍처를 공통으로 두면 PC 종류와 관계없이 이 주소를 고릅니다. SHA-256을 넣으면 에이전트가 받은 파일 해시를 확인합니다.' | translate }}
          </p>
          <div class="modal-actions">
            <button type="button" class="btn outline" @click="closeForm">{{ '취소' | translate }}</button>
            <button type="submit" class="btn" :disabled="saving">{{ '저장' | translate }}</button>
          </div>
        </form>
      </div>
    </template>
  </main-layout>
</template>

<script>
import { PATH } from '@/constants'
import MainLayout from '@/components/common/layout/MainLayout'
import MainHeader from '@/components/common/layout/MainHeader'

function emptyForm() {
  return {
    printerDriverPackageId: null,
    manufacturer: '',
    model: '',
    kind: 'printer',
    architecture: 'x64',
    downloadUrl: '',
    fileName: '',
    sha256: '',
    driverName: '',
    infFileName: '',
    useYn: 'Y',
  }
}

export default {
  name: 'PrinterDriverPackage',
  components: { MainLayout, MainHeader },
  data() {
    return {
      keyword: '',
      kind: '',
      architecture: '',
      useYn: '',
      page: 1,
      rowsPerPage: 50,
      totalCount: 0,
      rows: [],
      formOpen: false,
      saving: false,
      form: emptyForm(),
      requests: [],
      requestStatus: 'OPEN',
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.totalCount || 0) / this.rowsPerPage))
    },
    formTitle() {
      const key = this.form.printerDriverPackageId ? '드라이버 수정' : '드라이버 등록'
      return this.t(key)
    },
  },
  created() {
    this.load()
    this.loadRequests()
  },
  methods: {
    t(text) {
      return this.$options.filters && this.$options.filters.translate
        ? this.$options.filters.translate(text)
        : text
    },
    kindLabel(kind) {
      if (kind === 'scanner') return this.t('스캐너')
      if (kind === 'printer') return this.t('프린터')
      return kind || '-'
    },
    architectureLabel(architecture) {
      return architecture ? architecture : this.t('공통')
    },
    search() {
      this.page = 1
      this.load()
    },
    changePage(next) {
      this.page = next
      this.load()
    },
    setRequestStatus(status) {
      this.requestStatus = status
      this.loadRequests()
    },
    async loadRequests() {
      try {
        const res = await this.$request(PATH.MONITORING.PRINTER_DRIVER_REQUEST_LIST)
          .setObject({ status: this.requestStatus || null })
          .enqueue()
        const data = res?.data || res || []
        this.requests = Array.isArray(data) ? data : []
      } catch (e) {
        console.error(e)
        this.requests = []
      }
    },
    async completeRequest(row) {
      await this.updateRequest(row, 'DONE')
    },
    async reopenRequest(row) {
      await this.updateRequest(row, 'OPEN')
    },
    async updateRequest(row, status) {
      try {
        await this.$request(PATH.MONITORING.PRINTER_DRIVER_REQUEST_STATUS)
          .setObject({
            printerDriverRequestId: row.printerDriverRequestId,
            status: status,
            memo: row.memo || null,
          })
          .catch()
          .enqueue()
        this.loadRequests()
      } catch (e) {
        console.error(e)
        const message = e?.response?.data?.hri || this.t('신청 상태를 바꾸지 못했습니다.')
        this.$toasted?.error?.(message)
      }
    },
    async load() {
      try {
        const res = await this.$request(PATH.MONITORING.PRINTER_DRIVER_LIST)
          .setObject({
            keyword: this.keyword || null,
            kind: this.kind || null,
            architecture: this.architecture || null,
            useYn: this.useYn || null,
            page: this.page,
            rows: this.rowsPerPage,
          })
          .enqueue()
        const data = res?.data || res || {}
        this.rows = Array.isArray(data.list) ? data.list : []
        this.totalCount = Number(data.totalCount || 0)
      } catch (e) {
        console.error(e)
        this.rows = []
        this.totalCount = 0
      }
    },
    openCreate() {
      this.form = emptyForm()
      this.formOpen = true
    },
    openEdit(row) {
      this.form = {
        printerDriverPackageId: row.printerDriverPackageId,
        manufacturer: row.manufacturer || '',
        model: row.model || '',
        kind: row.kind || 'printer',
        architecture: row.architecture || '',
        downloadUrl: row.downloadUrl || '',
        fileName: row.fileName || '',
        sha256: row.sha256 || '',
        driverName: row.driverName || '',
        infFileName: row.infFileName || '',
        useYn: row.useYn === 'N' ? 'N' : 'Y',
      }
      this.formOpen = true
    },
    closeForm() {
      if (this.saving) return
      this.formOpen = false
    },
    async save() {
      this.saving = true
      try {
        await this.$request(PATH.MONITORING.PRINTER_DRIVER_SAVE)
          .setObject({
            ...this.form,
            fileName: this.form.fileName || null,
            sha256: this.form.sha256 || null,
            driverName: this.form.driverName || null,
            infFileName: this.form.infFileName || null,
          })
          .catch()
          .enqueue()
        this.$toasted?.show?.(this.t('드라이버를 저장했습니다.'))
        this.formOpen = false
        this.load()
      } catch (e) {
        console.error(e)
        const message = e?.response?.data?.hri || this.t('저장에 실패했습니다.')
        this.$toasted?.error?.(message)
      } finally {
        this.saving = false
      }
    },
    async remove(row) {
      if (!window.confirm(this.t('이 드라이버를 삭제할까요?'))) return
      try {
        await this.$request(PATH.MONITORING.PRINTER_DRIVER_DELETE)
          .setObject({ printerDriverPackageId: row.printerDriverPackageId })
          .catch()
          .enqueue()
        this.$toasted?.show?.(this.t('드라이버를 삭제했습니다.'))
        this.load()
      } catch (e) {
        console.error(e)
        const message = e?.response?.data?.hri || this.t('삭제에 실패했습니다.')
        this.$toasted?.error?.(message)
      }
    },
  },
}
</script>

<style scoped>
.driver-page {
  padding: 16px 20px 28px;
}
.todo {
  margin: 0 0 18px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}
.todo-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.todo-head h3 {
  margin: 0;
  font-size: 15px;
}
.btn.outline.on {
  border-color: #2563eb;
  color: #2563eb;
}
.hint,
.form-hint {
  margin: 0 0 12px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
.tools {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.search-input,
.filter,
.modal input,
.modal select {
  height: 32px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0 10px;
  background: #fff;
}
.search-input {
  min-width: 280px;
}
.filter {
  min-width: 120px;
}
.btn {
  height: 32px;
  border: 0;
  border-radius: 4px;
  background: #2563eb;
  color: #fff;
  padding: 0 12px;
  cursor: pointer;
}
.btn.outline {
  background: #fff;
  color: #1e293b;
  border: 1px solid #cbd5e1;
}
.btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.table-wrap {
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}
.list-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.list-table th,
.list-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  vertical-align: top;
}
.list-table th {
  background: #f8fafc;
  white-space: nowrap;
}
.url {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nowrap {
  white-space: nowrap;
}
.empty {
  text-align: center;
  color: #64748b;
  padding: 28px 10px;
}
.actions {
  white-space: nowrap;
}
.link-btn {
  border: 0;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
  padding: 0 6px 0 0;
}
.link-btn.danger {
  color: #dc2626;
}
.pager {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 16px;
  z-index: 30;
  overflow: auto;
}
.modal {
  width: min(640px, 100%);
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.modal h3 {
  margin: 0 0 4px;
  font-size: 16px;
}
.modal label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #334155;
}
.modal input,
.modal select {
  width: 100%;
  height: 34px;
}
.split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.check {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
.check input {
  width: auto;
  height: auto;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
</style>
